import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Select,
  Typography,
  Space,
  message,
  Card,
  Input,
  Row,
  Col,
  Tag,
  Spin,
  Tooltip,
  Divider,
} from "antd";
import { CopyOutlined, SearchOutlined } from "@ant-design/icons";
import { lawsuitTemplates } from "./tamplate/TamplateLawsuit";
import MotionHoc from "../../../utils/MotionHoc";
import {
  HEADERS_EXPORT_BEN,
  POST_DETAIL_PAYMENT,
  POST_DETAIL_PAYMENT_LEASING,
} from "../../API/apiUrls";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/th";
import ThaiDatePicker from "../../../hook/ThaiDatePicker";

const { Paragraph, Text } = Typography;
const { Option } = Select;

// ─────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────

const TH_MONTHS = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

/** DD MMMM YYYY (พ.ศ.) */
const fmtDate = (d) => {
  if (!d) return "";
  const m = dayjs(d);
  return `${m.date()} ${TH_MONTHS[m.month()]} ${m.year() + 543}`;
};

const dayOfMonth = (d) => (d ? String(dayjs(d).date()) : "");

/** จัดรูปตัวเลข: ทศนิยมเมื่อมีเศษ */
const fmt = (n) => {
  const num = Number(n ?? 0);
  return num % 1 === 0
    ? num.toLocaleString("th-TH")
    : num.toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};

/** แปลงตัวเลขเป็นคำอ่านภาษาไทย */
const numberToThaiText = (n) => {
  const num = Math.round(Number(n ?? 0) * 100) / 100;
  if (isNaN(num) || num === 0) return "ศูนย์บาทถ้วน";

  const D = [
    "ศูนย์",
    "หนึ่ง",
    "สอง",
    "สาม",
    "สี่",
    "ห้า",
    "หก",
    "เจ็ด",
    "แปด",
    "เก้า",
  ];
  const P = ["", "สิบ", "ร้อย", "พัน", "หมื่น", "แสน"];

  const grp = (s) => {
    const str = s.replace(/^0+/, "");
    if (!str) return "";
    const len = str.length;
    let out = "";
    for (let i = 0; i < len; i++) {
      const d = parseInt(str[i]);
      const p = len - 1 - i;
      if (d === 0) continue;
      if (p === 1) {
        out += d === 1 ? "สิบ" : d === 2 ? "ยี่สิบ" : `${D[d]}สิบ`;
      } else if (p === 0 && len > 1 && parseInt(str[len - 2]) > 0 && d === 1) {
        out += "เอ็ด";
      } else {
        out += D[d] + (p <= 5 ? P[p] : "");
      }
    }
    return out;
  };

  const [wStr, dStr = "00"] = num.toFixed(2).split(".");
  const w = parseInt(wStr);
  const mil = Math.floor(w / 1_000_000);
  const rem = w % 1_000_000;

  let result = "";
  if (mil > 0) result += grp(String(mil)) + "ล้าน";
  if (rem > 0) result += grp(String(rem).padStart(6, "0"));
  if (!result) result = "ศูนย์";
  result += "บาท";

  const dec = parseInt(dStr.slice(0, 2).padEnd(2, "0"));
  result += dec === 0 ? "ถ้วน" : grp(String(dec).padStart(2, "0")) + "สตางค์";
  return result;
};

// ─────────────────────────────────────────────
// API & ประเภทสัญญา
// ─────────────────────────────────────────────

const LEASING_TYPES = new Set(["SFHP", "VSFHP"]);

const getPaymentApi = (typeValue) =>
  LEASING_TYPES.has(typeValue)
    ? POST_DETAIL_PAYMENT_LEASING
    : POST_DETAIL_PAYMENT;

/** ตรวจจับประเภทคำฟ้องจากเลขสัญญา */
const detectLoanType = (contno) => {
  const prefix = String(contno).split("-")[0];
  if (["1", "N", "V1"].includes(prefix)) return "mortgage";
  if (["2", "8", "V2", "V8", "73", "5", "6"].includes(prefix))
    return "hirePurchase";
  if (["3", "V3"].includes(prefix)) return "carTitleLoan";
  console.log("prefix", prefix, contno);

  return null;
};

const mapHirePurchase = (data, attorney) => {
  const { loan = {}, result = [], invtran = {}, guarantor = [] } = data;

  const first = result[0] || {};
  const unpaidFirst = parseInt((loan.unpaid_count || "1").split("-")[0]) || 1;
  const missedRow = result.find((r) => r.nopay === unpaidFirst) || first;
  const dueDate = missedRow.ddate || "";

  const dok = (loan.balanc || 0) - (loan.ncshprc || 0);
  const litigationFunds = loan.ncshprc || 0;
  // ncshprc + ค่าติดตาม 2,000 + ค่าขาดประโยชน์
  const litigation =
    (loan.ncshprc || 0) + 2_000 + (loan.total_used_net_pay || 0);
  const defendentCount = 1 + guarantor.length;
  const defendentNo = guarantor
    .map((_, i) => `จำเลยที่ ${i + 2}`)
    .join(" และ ");
  const considerDate = String(dayjs().diff(dayjs(dueDate), "year") || 0);

  return {
    attorneyName: attorney,
    // ข้อ 2
    sdate: fmtDate(loan.sdate),
    ncshprc: fmt(loan.ncshprc),
    balanc: fmt(loan.balanc),
    dok: fmt(dok),
    vat: fmt(0),
    tot_upay: fmt(loan.tot_upay),
    tnopay: String(loan.tnopay || ""),
    ddate: fmtDate(first.ddate), // วันครบกำหนดงวดแรก
    day: dayOfMonth(first.ddate), // วันที่ของเดือน
    // รถ
    type: (invtran.type || "").trim(),
    strno: (invtran.strno || "").trim(),
    regno: (invtran.regno || "").trim(),
    dorecv: (invtran.dorecv || "").trim(),
    engno: (invtran.engno || "").trim(),
    // ข้อ 3
    smpay: fmt(loan.smpay), // ยอดชำระแล้วรวม
    unpaid_count: String(unpaidFirst), // งวดที่เริ่มผิดนัด
    dueDate: fmtDate(dueDate), // วันครบกำหนดงวดที่ผิดนัด
    remaining_debt: fmt(loan.remaining_debt),
    // ข้อ 4
    total_used_net_pay: fmt(loan.total_used_net_pay),
    litigation: fmt(litigation),
    considerDate,
    // ข้อ 5
    litigationFunds: fmt(litigationFunds),
    // จำเลย
    defendentCount,
    defendentNo,
    // อ่านค่า
    convertThaiRemainingDebt: numberToThaiText(loan.remaining_debt || 0),
    convertThaincshprc: numberToThaiText(loan.ncshprc || 0),
    convertThaiLitigationFunds: numberToThaiText(litigationFunds),
    convertThaiLitigation: numberToThaiText(litigation),
  };
};

/**
 * จำนอง (1-xxxx)
 * API คืน: { resultdata[], loan, invtran(ข้อมูลที่ดิน), customer[], guarantor[], arpay[] }
 */
const mapMortgage = (data, attorney, contno) => {
  const { loan = {}, resultdata = [], invtran = {}, guarantor = [] } = data;

  const totalPay = resultdata.reduce((s, r) => s + (r.NETPAY || 0), 0);
  const totalTon = resultdata.reduce((s, r) => s + (r.DUETONEFF || 0), 0);
  const totalDok = resultdata.reduce((s, r) => s + (r.DUEINTEFF || 0), 0);
  const tonkong = loan.tonkong || 0;
  const dok = loan.dok || 0;
  const litigationFunds = tonkong + dok;
  const defendentCount = 1 + guarantor.length;
  const defendentNo = guarantor
    .map((_, i) => `จำเลยที่ ${i + 2}`)
    .join(" และ ");

  return {
    attorneyName: attorney,
    // ข้อ 2
    sdate: fmtDate(loan.sdate),
    ncshprc: fmt(loan.ncshprc),
    totUpay: fmt(loan.totUpay),
    tnopay: String(loan.tnopay || ""),
    startdate: fmtDate(loan.startdate),
    enddate: fmtDate(loan.enddate),
    day: dayOfMonth(loan.startdate),
    searchInput: contno, // เลขสัญญาในเอกสาร
    // ที่ดิน (invtran เก็บข้อมูลที่ดิน)
    type: (invtran.type || "").trim(), // จังหวัด
    modeldes: (invtran.modeldes || "").trim(), // อำเภอ
    baabdes: (invtran.baabdes || "").trim(), // ตำบล
    strno: (invtran.strno || "").trim(), // เลขที่ดิน
    dorecv: (invtran.dorecv || "").trim(), // โฉนดเลขที่
    // ข้อ 3
    totalPay: fmt(totalPay),
    totalTon: fmt(totalTon),
    totalDok: fmt(totalDok),
    tonkong: fmt(tonkong),
    // ข้อ 4
    dok: fmt(dok),
    litigationFunds: fmt(litigationFunds),
    // จำเลย
    defendentCount,
    defendentNo,
    // อ่านค่า
    convertThaiTonkong: `(${numberToThaiText(tonkong)})`,
    convertThailackTonkong: numberToThaiText(tonkong),
    convertThaiLitigationFunds: numberToThaiText(litigationFunds),
  };
};

/**
 * กู้ยืมคู่มือรถ (3-xxxx)
 * API คืน: { resultdata[], loan(+kangdok), invtran, customer[], guarantor[], arpay[] }
 */
const mapCarTitleLoan = (data, attorney) => {
  const { loan = {}, resultdata = [], invtran = {}, guarantor = [] } = data;

  const totalPay = resultdata.reduce((s, r) => s + (r.NETPAY || 0), 0);
  const totalTon = resultdata.reduce((s, r) => s + (r.DUETONEFF || 0), 0);
  const totalDok = resultdata.reduce((s, r) => s + (r.DUEINTEFF || 0), 0);
  const tonkong = loan.tonkong || 0;
  const dok = loan.dok || 0;
  const kangdok = loan.kangdok || 0; // ดอกเบี้ยค้างสะสม
  const litigationFunds = tonkong + dok + kangdok;
  const defendentCount = 1 + guarantor.length;
  const defendentNo = guarantor
    .map((_, i) => `จำเลยที่ ${i + 2}`)
    .join(" และ ");

  return {
    attorneyName: attorney,
    // ข้อ 2
    sdate: fmtDate(loan.sdate),
    ncshprc: fmt(loan.ncshprc),
    totUpay: fmt(loan.totUpay || loan.hdpayment),
    tnopay: String(loan.tnopay || ""),
    startdate: fmtDate(loan.startdate),
    day: dayOfMonth(loan.startdate),
    // รถ
    type: (invtran.type || "").trim(),
    regno: (invtran.regno || "").trim(),
    dorecv: (invtran.dorecv || "").trim(),
    engno: (invtran.engno || "").trim(),
    strno: (invtran.strno || "").trim(),
    // ข้อ 3
    totalPay: fmt(totalPay),
    totalTon: fmt(totalTon),
    totalDok: fmt(totalDok),
    tonkong: fmt(tonkong),
    // ข้อ 4
    dok: fmt(dok),
    kangdok: fmt(kangdok),
    litigationFunds: fmt(litigationFunds),
    // จำเลย
    defendentCount,
    defendentNo,
    // อ่านค่า
    convertThaiTonkong: `(${numberToThaiText(tonkong)})`,
    convertThailackTonkong: numberToThaiText(tonkong),
    convertThaiLitigationFunds: numberToThaiText(litigationFunds),
  };
};

const mapLoanToTemplateData = (data, type, opts = {}) => {
  const { attorney = "", contno = "" } = opts;
  console.log("attorney--->", attorney);
  console.log("contno", contno, type);

  if (type === "hirePurchase") return mapHirePurchase(data, attorney);
  if (type === "mortgage") return mapMortgage(data, attorney, contno);
  if (type === "carTitleLoan") return mapCarTitleLoan(data, attorney);
  return {};
};

// ─────────────────────────────────────────────
// Highlight renderer
// ─────────────────────────────────────────────

const renderHighlightedText = (text, data) =>
  text.split(/({{.*?}})/g).map((part, i) => {
    if (!part.startsWith("{{") || !part.endsWith("}}"))
      return <span key={i}>{part}</span>;
    const key = part.slice(2, -2).trim();
    const value = data[key];
    const isMissing = value == null;
    return (
      <Text
        key={i}
        style={{
          color: isMissing ? "#ff4d4f" : "#1890ff",
          fontWeight: "bold",
          background: isMissing ? "#fff1f0" : "#e6f7ff",
          padding: "0 2px",
          borderRadius: 3,
        }}
      >
        {isMissing ? `[ไม่มีข้อมูล: ${key}]` : String(value)}
      </Text>
    );
  });

// ─────────────────────────────────────────────
// Component หลัก
// ─────────────────────────────────────────────

const Main = () => {
  const FNAME = localStorage.getItem("FNAME") || "";
  const LNAME = localStorage.getItem("LNAME") || "";
  const userCompany = localStorage.getItem("COMPANY_ID");
  const attorney = `${FNAME} ${LNAME}`;
  console.log("attorney", attorney);

  const [selectedType, setSelectedType] = useState(null);
  const [loadingLoan, setLoadingLoan] = useState(false);
  const [loanData, setLoanData] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [templateData, setTemplateData] = useState({});
  const [optionsDataType, setOptionsDataType] = useState([]);
  const [dataTypeSelect, setDataTypeSelect] = useState(null); // ค่าที่ใช้ส่ง API
  const [dataTypeSelectLabel, setDataTypeSelectLabel] = useState(null); // label ที่แสดง
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [arrow, setArrow] = useState("Show");

  const mergedArrow = useMemo(() => {
    if (arrow === "Hide") return false;
    if (arrow === "Show") return true;
    return { pointAtCenter: true };
  }, [arrow]);

  useEffect(() => {
    const opts =
      userCompany === "3"
        ? [
            { value: "KSM", label: "KSM(ที่ดิน)" },
            { value: "RPSL", label: "P-Loan" },
          ]
        : [
            { value: "LSFHP", label: "สัญญา 1" },
            { value: "VSFHP", label: "สัญญา 2" },
            { value: "PSFHP", label: "สัญญา 3(เก่า)" },
            { value: "RPSL", label: "สัญญา 3(ใหม่)" },
            { value: "SFHP_LAND", label: "สัญญา 8(ที่ดิน)" },
            { value: "SFHP_CAR", label: "สัญญา 8(รถ)" },
          ];
    setOptionsDataType(opts);
  }, [userCompany]);

  const handleTypeContno = (value, option) => {
    const normalized = ["SFHP_CAR", "SFHP_LAND"].includes(value)
      ? "SFHP"
      : value;
    setDataTypeSelect(normalized);
    setDataTypeSelectLabel(option);
  };

  const queryData = async (contno, typeValue) => {
    setLoadingLoan(true);
    try {
      const res = await axios.post(
        getPaymentApi(typeValue),
        { contno, todate: dayjs(date).format("YYYY-MM-DD"), type: typeValue },
        { headers: HEADERS_EXPORT_BEN },
      );

      // รองรับทั้ง Array และ Object
      const data = Array.isArray(res?.data) ? res.data[0] : res?.data;
      if (!data) {
        message.error("ไม่มีเลขที่สัญญาที่ค้นหา");
        return;
      }

      const loanType = detectLoanType(contno);
      setLoanData(data);
      setSelectedType(loanType);
      setTemplateData(
        mapLoanToTemplateData(data, loanType, { attorney, contno }),
      );
    } catch (err) {
      const is404 = err?.response?.status === 404;
      message.error(
        is404 ? "ไม่มีเลขที่สัญญาที่ค้นหา" : "เกิดข้อผิดพลาดในการดึงข้อมูล",
      );
    } finally {
      setLoadingLoan(false);
    }
  };

  const onSearch = () => {
    const contno = searchInput.trim();
    if (!contno) {
      message.warning("กรุณากรอกเลขสัญญา");
      return;
    }
    if (!dataTypeSelect) {
      message.warning("กรุณาเลือกประเภทสัญญา");
      return;
    }
    queryData(contno, dataTypeSelect);
  };

  const handleChange = (value) => {
    setDate(dayjs(value).format("YYYY-MM-DD"));
  };

  const currentSections = lawsuitTemplates[selectedType] ?? [];

  const handleCopySection = (section) => {
    let content = section.content;
    Object.entries(templateData).forEach(([key, val]) => {
      content = content.replace(
        new RegExp(`{{${key}}}`, "g"),
        val != null ? String(val) : `[ไม่มีข้อมูล ${key}]`,
      );
    });
    navigator.clipboard
      .writeText(content)
      .then(() => message.success(`คัดลอก "${section.title}" แล้ว`))
      .catch(() => message.error("ไม่สามารถคัดลอกได้"));
  };

  return (
    <>
      {/* Search */}
      <Card
        style={{
          marginBottom: 8,
          borderRadius: 16,
          border: "1px solid #e8c84a",
          background: "linear-gradient(135deg, #fffdf0 0%, #fffae0 100%)",
          boxShadow: "0 2px 12px rgba(239, 164, 32, 0.12)",
          overflow: "hidden",
        }}
        styles={{ body: { padding: "14px 18px" } }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              width: 4,
              height: 18,
              borderRadius: 2,
              background: "#efa420",
            }}
          />
          <span style={{ fontSize: 13, fontWeight: 600, color: "#b07210" }}>
            ค้นหาสัญญา
          </span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{ fontSize: 14, color: "#9a7000", whiteSpace: "nowrap" }}
            >
              วันที่คิดดอกเบี้ยถึง
            </span>
            <Tooltip
              placement="bottom"
              title="วันที่คิดดอกเบี้ยถึง"
              arrow={mergedArrow}
            >
              <ThaiDatePicker
                size="large"
                defaultValue={dayjs()}
                onChange={handleChange}
              />
            </Tooltip>
          </div>

          <Divider
            type="vertical"
            style={{ height: 24, borderColor: "#e8c84a" }}
          />

          <Select
            size="large"
            value={dataTypeSelect}
            onChange={handleTypeContno}
            popupMatchSelectWidth={false}
            options={optionsDataType}
            placeholder="ประเภทสัญญา"
            style={{ minWidth: 160 }}
          />

          <Divider
            type="vertical"
            style={{ height: 24, borderColor: "#e8c84a" }}
          />

          <Input
            placeholder="ค้นหาสัญญา..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onPressEnter={onSearch}
            size="small"
            style={{ width: 180, borderRadius: 8 }}
            suffix={
              <SearchOutlined style={{ color: "#c8a020", fontSize: 14 }} />
            }
          />

          <Button
            size="large"
            type="primary"
            onClick={onSearch}
            style={{
              background: "#efa420",
              borderColor: "#efa420",
              borderRadius: 8,
              fontWeight: 600,
              boxShadow: "0 2px 6px rgba(239, 164, 32, 0.35)",
            }}
          >
            ค้นหา
          </Button>

          <Divider
            type="vertical"
            style={{ height: 24, borderColor: "#e8c84a" }}
          />

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{ fontSize: 14, color: "#9a7000", whiteSpace: "nowrap" }}
            >
              ประเภทคำฟ้อง
            </span>
            <Select
              value={selectedType}
              onChange={setSelectedType}
              size="large"
              style={{ width: 180 }}
              disabled={!loanData}
              placeholder="เลือกประเภทคำฟ้อง"
            >
              <Option value="mortgage">จำนองที่ดิน</Option>
              <Option value="hirePurchase">เช่าซื้อรถยนต์</Option>
              <Option value="carTitleLoan">กู้ยืมคู่มือรถ</Option>
            </Select>
          </div>
        </div>
      </Card>

      {/* Template Sections */}
      <Spin spinning={loadingLoan}>
        <Row gutter={[0, 12]}>
          {currentSections.map((section) => (
            <Col span={24} key={section.key}>
              <Card
                size="small"
                style={{
                  borderRadius: 10,
                  border: "1px solid #d9d9d9",
                  background: "#fafafa",
                }}
                title={
                  <Space>
                    <Tag color="blue" style={{ borderRadius: 10 }}>
                      ข้อ {section.key}
                    </Tag>
                    <Text strong style={{ fontSize: 18, color: "#1a365d" }}>
                      {section.title.replace(/.*?\d\.\s*/, "")}
                    </Text>
                  </Space>
                }
                extra={
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => handleCopySection(section)}
                    disabled={!loanData}
                  >
                    คัดลอก
                  </Button>
                }
              >
                <Paragraph
                  style={{
                    whiteSpace: "pre-wrap",
                    fontSize: 16,
                    lineHeight: 2,
                    margin: 0,
                    fontFamily: "Sarabun, sans-serif",
                  }}
                >
                  {loanData ? (
                    renderHighlightedText(section.content, templateData)
                  ) : (
                    <Text type="secondary">— ค้นหาสัญญาเพื่อแสดงข้อมูล —</Text>
                  )}
                </Paragraph>
              </Card>
            </Col>
          ))}
        </Row>
      </Spin>
    </>
  );
};

const FormLawsuit = MotionHoc(Main);
export default FormLawsuit;
